import { useLearningEvolution, useSuccessRates, useConfidenceLevels, useApproveLearning, useRejectLearning } from '@/api/experiments'

export default function LearningEvolution() {
  const { data: learningsData, isLoading: learningsLoading } = useLearningEvolution()
  const { data: ratesData } = useSuccessRates()
  const { data: levels } = useConfidenceLevels()
  const approveMut = useApproveLearning()
  const rejectMut = useRejectLearning()

  const learnings = learningsData?.learnings ?? []
  const rates = ratesData?.rates ?? []

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Strategie-Evolution</h1>

      <div className="grid grid-cols-3 gap-4">
        {/* Erfolgsquoten */}
        <div className="col-span-1 space-y-4">
          <div className="rounded-lg border p-4">
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">Erfolgsquoten pro Typ</h3>
            {rates.length === 0 ? (
              <p className="text-sm text-muted-foreground">Noch keine Daten</p>
            ) : (
              <div className="space-y-3">
                {rates.map((r, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium">{String(r.experiment_type)}</span>
                      <span>{String(r.success_rate)}% ({String(r.total)})</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full bg-green-500" style={{ width: `${Number(r.success_rate)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Confidence-Stufen */}
          {levels && (
            <div className="rounded-lg border p-4">
              <h3 className="text-sm font-semibold text-muted-foreground mb-3">Confidence-Stufe</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Aktuelle Stufe:</span>
                  <span className="font-medium">{levels.currentMode}</span>
                </div>
                <div className="flex justify-between">
                  <span>Evaluiert:</span>
                  <span>{levels.evaluatedTotal}</span>
                </div>
                <div className="flex justify-between">
                  <span>Erfolgsquote 90d:</span>
                  <span>{levels.successRate90d}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Shadow-Trefferquote:</span>
                  <span>{levels.shadowAccuracy}%</span>
                </div>
                {levels.nextLevel && (
                  <div className="mt-3 rounded bg-blue-50 p-2 text-xs">
                    <span className="font-semibold">Nächste Stufe: {levels.nextLevel}</span>
                    {levels.canUpgrade
                      ? <span className="ml-2 text-green-600">Kriterien erfüllt</span>
                      : (
                        <div className="mt-1">
                          <span>Low-Risk: {levels.criteria.lowRiskAuto.current}/{levels.criteria.lowRiskAuto.required} Exp., {levels.criteria.lowRiskAuto.successCurrent}%/{levels.criteria.lowRiskAuto.successRequired}% Erfolg</span>
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Learnings-Timeline */}
        <div className="col-span-2 rounded-lg border p-4">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">Learnings</h3>
          {learningsLoading ? (
            <p className="text-sm text-muted-foreground">Laden...</p>
          ) : learnings.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keine Learnings — Learnings werden nach der ersten Experiment-Auswertung generiert.</p>
          ) : (
            <div className="space-y-4">
              {learnings.map(l => (
                <div key={l.id} className="rounded border p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex gap-2 mb-1">
                        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-mono">{l.experimentType}</span>
                        <span className="rounded bg-blue-100 px-2 py-0.5 text-xs">{l.learningType}</span>
                        <span className={`rounded px-2 py-0.5 text-xs ${l.status === 'proposed' ? 'bg-yellow-100 text-yellow-800' : l.status === 'approved' ? 'bg-green-100 text-green-800' : l.status === 'applied' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100'}`}>
                          {l.status}
                        </span>
                      </div>
                      <p className="text-sm">{l.description}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{new Date(l.createdAt).toLocaleString('de-DE')}</p>
                    </div>
                    {l.status === 'proposed' && (
                      <div className="flex gap-1 shrink-0">
                        <button onClick={() => approveMut.mutate(l.id)} className="rounded bg-green-100 px-2 py-1 text-xs text-green-700 hover:bg-green-200">Approve</button>
                        <button onClick={() => rejectMut.mutate(l.id)} className="rounded bg-red-100 px-2 py-1 text-xs text-red-700 hover:bg-red-200">Reject</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
